import {
  constructPopulatedTheme,
  defaultThemeConfig,
  a11yTheme,
  deuteranopiaTheme,
  protanopiaTheme,
  tritanopiaTheme,
  monochromacyTheme,
  ThemeInterface,
  ThemeConfigInterface
} from '@embracesbs/helpers'
import { AccessibilityTheme, UserPreferences } from '../Components/Context/PreferencesContext'

// IMPORTANT: the surface hexes below (light/background/border/primary and the
// sidebar colors) must stay in sync with the body[data-theme='dark'] variables
// in src/index.css — the design-system theme and the app-shell CSS variables
// are two halves of the same dark look.
const darkThemeConfig: ThemeConfigInterface = {
  meta: { version: 1 },
  color: {
    primary: '#6C8CFF',
    secondary: '#2FBFB7',
    tertiary: '#9B77E8',
    // Inverting light/dark is what makes the design system render dark
    // (useIsDarkTheme compares their luminance): 'light' = surface, 'dark' = text.
    light: '#22252A',
    dark: '#F2F3F5',
    success: '#69CC89',
    warning: '#F9A339',
    danger: '#F0697A',
    background: '#191B1F',
    border: '#3A3D44',
    input: { borderColor: '#5A6070' }
    // support1..8 keep their defaults — they stay legible on dark surfaces.
  },
  ui: {
    button: {
      contained: {
        danger: {
          // The default config labels contained-danger buttons with 'light',
          // which in this inverted palette is the dark surface color — giving
          // dark-on-red labels. 'dark' is this theme's near-white.
          color: 'dark'
        }
      }
    }
  },
  suite: {
    sidebar: {
      // Color KEYS (resolved during constructPopulatedTheme), mirroring the
      // default config's 'light$3'. With the inverted palette 'dark$3' is a
      // 70%-alpha near-white — without this override the sidebar icons would
      // render dark-on-dark.
      iconColor: 'dark$3',
      iconHoverColor: 'dark$3',
      iconActiveColor: 'dark',
      iconActiveHoverColor: 'dark',
      background: '#101114',
      activeItemBackground: '#33363B'
    }
  }
}

// Built once at module level; ThemeController receives populated themes as-is.
export const darkTheme: ThemeInterface = constructPopulatedTheme(darkThemeConfig)

// The explicit light theme. ThemeController mutates the design system's
// global theme singleton (mutateGlobalTheme defaults to true), so switching
// away from dark MUST pass a fully-populated light theme — handing it
// `undefined` would leave the singleton carrying dark values and the app in a
// mixed light-shell/dark-components state.
export const lightTheme: ThemeInterface = constructPopulatedTheme(defaultThemeConfig as ThemeConfigInterface)

const accessibilityThemes: Record<Exclude<AccessibilityTheme, 'none'>, ThemeInterface> = {
  a11y: a11yTheme,
  deuteranopia: deuteranopiaTheme,
  protanopia: protanopiaTheme,
  tritanopia: tritanopiaTheme,
  monochromacy: monochromacyTheme
}

export type ShellMode = 'light' | 'dark'

// Precedence: an accessibility theme replaces the palette entirely and forces
// the light app shell — the shipped a11y themes are light-only, and we don't
// invent dark-a11y hybrids. Always returns a populated theme (see lightTheme).
export const resolveTheme = (
  preferences: UserPreferences,
  systemPrefersDark: boolean
): { themeObject: ThemeInterface; shellMode: ShellMode } => {
  if (preferences.accessibilityTheme !== 'none') {
    return { themeObject: accessibilityThemes[preferences.accessibilityTheme], shellMode: 'light' }
  }
  const isDark = preferences.theme === 'dark' || (preferences.theme === 'system' && systemPrefersDark)
  return isDark ? { themeObject: darkTheme, shellMode: 'dark' } : { themeObject: lightTheme, shellMode: 'light' }
}
